import { Link } from 'react-router-dom';
import { useState } from 'react';

const Carousel = ({ bathroomImages, tileImages }) => {
  const [activeCategory, setActiveCategory] = useState('bathroom');
  const [currentSlide, setCurrentSlide] = useState(0);

  const images = activeCategory === 'bathroom' ? bathroomImages : tileImages;

  const handleCategoryChange = (category) => {
    setActiveCategory(category);
    setCurrentSlide(0); // Reset to first slide when switching categories
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % images.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + images.length) % images.length);
  };

  return (
    <div className="relative w-full">
      {/* Category Toggle Buttons with Dynamic Background Images */}
      <div className="flex justify-center gap-4 mb-6">
        <button
          onClick={() => handleCategoryChange('bathroom')}
          className={`relative px-8 py-3 rounded-full font-bold transition-all duration-300 shadow-lg overflow-hidden ${
            activeCategory === 'bathroom'
              ? 'text-white scale-110 ring-2 ring-primary'
              : 'text-white scale-100'
          }`}
        >
          {/* Background Image - Changes with carousel slide when active */}
          <div 
            className="absolute inset-0 bg-cover bg-center transition-all duration-700"
            style={{ 
              backgroundImage: activeCategory === 'bathroom' 
                ? `url(${bathroomImages[currentSlide].src})` 
                : 'url(/media/billeder/Badeværelse/IMG_0089.jpeg)' 
            }}
          />
          {/* Overlay */}
          {activeCategory === 'bathroom' ? (
            <span className="absolute inset-0 bg-primary/30"></span>
          ) : (
            <span className="absolute inset-0 bg-black/50 hover:bg-black/40 transition-all duration-300"></span>
          )}
          <span className="relative z-10 drop-shadow-lg">Badeværelse</span>
        </button>
        
        <button
          onClick={() => handleCategoryChange('tiles')}
          className={`relative px-8 py-3 rounded-full font-bold transition-all duration-300 shadow-lg overflow-hidden ${
            activeCategory === 'tiles'
              ? 'text-white scale-110 ring-2 ring-primary'
              : 'text-white scale-100'
          }`}
        >
          {/* Background Image - Changes with carousel slide when active */}
          
          <div 
            className="absolute inset-0 bg-cover bg-center transition-all duration-700"
            style={{ 
              backgroundImage: activeCategory === 'tiles' 
                ? `url(${tileImages[currentSlide].src})` 
                : 'url(/media/billeder/Fliser/IMG_0047.jpeg)' 
            }}
          />
          {/* Overlay */}
          {activeCategory === 'tiles' ? (
            <span className="absolute inset-0 bg-primary/30"></span>
          ) : (
            <span className="absolute inset-0 bg-black/50 hover:bg-black/40 transition-all duration-300"></span>
          )}
          <span className="relative z-10 drop-shadow-lg">Fliser</span>
        </button>
      </div>

      {/* Carousel */}
      <div className="relative w-full h-[50vh] md:h-[60vh] lg:h-[70vh] overflow-hidden rounded-2xl shadow-2xl border-4 border-gray-700 bg-gradient-to-br from-gray-900 to-gray-800">
          {/* 🔹 Light overlay over background */}
  <div className="absolute inset-0 bg-white/10 pointer-events-none z-20"></div>
        {/* Image slides with gradient overlay */}
        {images.map((image, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-all duration-700 ${
              index === currentSlide ? 'opacity-100 scale-100' : 'opacity-0 scale-105'
            }`}
          >
            <img
              src={image.src}
              alt={image.title}
              className="w-full h-full object-cover"
            />
            {/* Gradient overlay for better text readability */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/30" />
          </div>
        ))}

        {/* Navigation Arrows */}
        <button
          onClick={prevSlide}
          className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 bg-white/10 backdrop-blur-md text-white p-2 md:p-3 rounded-full hover:bg-white/20 transition-all duration-300 shadow-xl border border-white/20 z-30 group"
          aria-label="Forrige"
        >
          <svg className="w-5 h-5 md:w-6 md:h-6 transform group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <button
          onClick={nextSlide}
          className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 bg-white/10 backdrop-blur-md text-white p-2 md:p-3 rounded-full hover:bg-white/20 transition-all duration-300 shadow-xl border border-white/20 z-30 group"
          aria-label="Næste"
        >
          <svg className="w-5 h-5 md:w-6 md:h-6 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M9 5l7 7-7 7" />
          </svg>
        </button>

        {/* Indicators */}
        <div className="absolute bottom-3 md:bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-30">
          {images.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`transition-all duration-300 rounded-full ${
                index === currentSlide 
                  ? 'bg-white w-8 md:w-10 h-2.5 md:h-3 shadow-lg' 
                  : 'bg-white/40 w-2.5 md:w-3 h-2.5 md:h-3 hover:bg-white/60'
              }`}
              aria-label={`Gå til slide ${index + 1}`}
            />
          ))}
        </div>
      </div>

      {/* Gallery Button - Below carousel */}
      <div className="flex justify-center mt-6">
        <Link to="/galleri">
          <button className="relative px-10 py-4 rounded-full font-bold text-white shadow-2xl transition-all duration-300 transform hover:scale-105 overflow-hidden group">
            <span className="absolute inset-0 bg-primary"></span>
            <span className="absolute inset-0 bg-primary opacity-0 group-hover:opacity-100 transition-opacity duration-500"></span>
            <span className="relative z-10 flex items-center gap-2">
              Se Galleri
              <svg className="w-5 h-5 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </span>
          </button>
        </Link>
      </div>
    </div>
  );
};

export default Carousel;